import type { FC } from 'react';
import type React from 'react';
import { createContext, useCallback, useContext, useMemo } from 'react';
import type { BoxProps } from '@mui/material';
import { Box } from '@mui/material';

import { AccordionContext } from 'src/components/common/Accordion';

import { styles } from './index.styles';
import { styles as filterColumnStyles } from 'src/components/followUp/FilterColumn/index.styles';

export interface AccordionItemProps extends BoxProps {
  itemName: string;
  nested?: boolean;
}

export const AccordionItemContext = createContext({
  isActive: true,
});

const AccordionItem: FC<AccordionItemProps> = ({
  children,
  itemName,
  sx = [],
  nested,
  ...props
}) => {
  const { level, activeItems, toggleActiveState } =
    useContext(AccordionContext);

  const { activeChildItems, isActive } = useMemo(
    () => ({
      activeChildItems: activeItems.slice(1),
      isActive: activeItems.includes(itemName),
    }),
    [activeItems, itemName],
  );

  const toggleActiveChildState = useCallback(
    (_itemName: string, _level: number = level + 1) => {
      toggleActiveState(_itemName, _level);
    },
    [toggleActiveState, level],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggleActiveState(itemName);
      }
    },
    [toggleActiveState, itemName],
  );

  return (
    <AccordionContext.Provider
      value={{
        level: level + 1,
        activeItems: activeChildItems,
        toggleActiveState: toggleActiveChildState,
      }}
    >
      <AccordionItemContext.Provider value={{ isActive }}>
        <Box
          id={itemName}
          onClick={() => toggleActiveState(itemName)}
          onKeyDown={handleKeyDown}
          role="menuitem"
          tabIndex={0}
          sx={[
            styles.accordionItem(isActive),
            ...(nested ? [] : [filterColumnStyles.filterColumnSection]),
            ...(Array.isArray(sx) ? sx : [sx]),
          ]}
          {...props}
          className="AccordionItem-root"
        >
          {children}
        </Box>
      </AccordionItemContext.Provider>
    </AccordionContext.Provider>
  );
};

export default AccordionItem;
